import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, Legend, Tooltip } from 'recharts';
import { Grid, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography, useColorScheme, Tooltip as MuiTooltip, Popover, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { changeColor } from './common';
import PropTypes from 'prop-types';

interface IData {
    [key: string]: any;
}

const RCPieChart: React.FC<IData> = (props) => {
    const { data, dataKey, seriesList, width, height, customLabel, customTooltip, legend, elevation, chartSize, convertData: customConvertData, showLegendCounts, enableLegendClickTooltip } = props;
    const CustomLabel = customLabel || undefined;
    const CustomTooltip = customTooltip || undefined;
    const RADIAN = Math.PI / 180;
    let { mode } = useColorScheme();

    const defaultConvertData = (item) => seriesList.map((serie: any) => ({ name: serie.name, value: item[serie.dataKey], fill: serie.fill }));

    const convertData = customConvertData || defaultConvertData;

    const DefaultLabel = (labelProps: any) => {
        const { cx, cy, midAngle, innerRadius, outerRadius, percent } = labelProps;
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (<>
            {percent && <text
                x={x}
                y={y}
                fill="white"
                textAnchor={x > cx ? 'start' : 'end'}
                dominantBaseline="central"
                style={{ fontSize: '13px' }}
            >
                {`${(percent * 100).toFixed(0)}%`}
            </text>}
        </>
        );
    };

    const DefaultTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            return (
                <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 40 }} size="small" aria-label="pie chart tooltip">
                        <TableBody>
                            {payload.map((row: any) => (
                                <TableRow key={`pietooltip-${row.name}`}>
                                    <TableCell align='right'>
                                        <svg height={6 * 2} width={6 * 2}>
                                            <circle cx={6} cy={6} r={6} style={{ fill: row.payload.fill }} />
                                        </svg>
                                    </TableCell>
                                    <TableCell align="left">{row.name}</TableCell>
                                    <TableCell align="right">{row.value}</TableCell>

                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            );
        } else return null;
    };

    const [activeLegend, setActiveLegend] = useState(null);
    const [legendAnchorEl, setLegendAnchorEl] = useState(null);

    const seriesMap = useMemo(() => new Map(seriesList.map((s: any) => [s.name, s])), [seriesList]);

    const handleLegendClick = (event, serie) => {
        if (!enableLegendClickTooltip) return;
        if (!serie || !serie.bypassInfo) return; // disable popover if no bypass info
        setActiveLegend(serie);
        setLegendAnchorEl(event.currentTarget);
    };

    const renderColorText = (value, entry) => {
        const serie = seriesMap.get(value) || seriesMap.get(entry?.value) || null;
        const count = showLegendCounts && serie && serie.venueCount !== null && serie.venueCount !== undefined ? serie.venueCount : null;
        const baseText = value;
        const fullDisplay = count !== null ? `${baseText} (${count})` : baseText; // visible text now includes count
        const titleText = fullDisplay; // tooltip shows same (can expand later)

        // Truncate fullDisplay if too long
        const maxLength = 45;
        let visible = fullDisplay;
        if (visible.length > maxLength) {
            let breakPoint = visible.lastIndexOf(' ', maxLength);
            if (breakPoint === -1) breakPoint = maxLength;
            visible = visible.substring(0, breakPoint) + '...';
        }
        	const hasBypass = !!(serie && serie.bypassInfo);
        	const isActive = enableLegendClickTooltip && hasBypass && activeLegend && activeLegend.name === serie.name;

        return (
            <MuiTooltip arrow placement="right" title={titleText} disableInteractive>
                <span
                    onClick={(e) => hasBypass && handleLegendClick(e, serie)}
                    	style={{
                        color: changeColor(mode),
                        wordWrap: 'break-word',
                        lineHeight: '1.2',
                        fontSize: '13px',
                        cursor: enableLegendClickTooltip && hasBypass ? 'pointer' : 'default',
                        fontWeight: isActive ? 600 : 400,
                        textDecoration: isActive ? 'underline' : 'none',
                        opacity: hasBypass ? 1 : 0.55,
                    }}
                >
                    {visible}
                </span>
            </MuiTooltip>
        );
    };

    return (
        <>
            {data.map((item: any, dIndex) => {
                let newItem = convertData(item);
                return <Grid item  key={`piechar-${dIndex}`}>
                    <Paper elevation={elevation} style={{ textAlign: 'center', padding: '10px' }}>
                        <Typography variant="h5" textAlign={'center'}>{item[dataKey]}</Typography>
                        <PieChart width={width} height={height}>
                            <Legend {...legend} formatter={renderColorText} />
                            <Tooltip content={CustomTooltip ? <CustomTooltip /> : <DefaultTooltip />} />
                            <Pie
                                data={newItem}
                                dataKey={'value'}
                                cx="50%"
                                cy="50%"
                                outerRadius={chartSize}
                                label={CustomLabel ? <CustomLabel /> : <DefaultLabel />}//https://recharts.org/en-US/examples/PieChartWithCustomizedLabel
                                isAnimationActive={false}
                                labelLine={false}
                            >
                                {newItem.map((obj: any, index) => (
                                    <Cell key={`cell-${index}`} fill={obj.fill} />
                                ))}
                            </Pie>
                        </PieChart>
                        {enableLegendClickTooltip && CustomTooltip && (
                            <Popover
                                open={Boolean(activeLegend && activeLegend.bypassInfo)}
                                anchorEl={legendAnchorEl}
                                onClose={() => { setActiveLegend(null); setLegendAnchorEl(null); }}
                                anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'center', horizontal: 'left' }}
                                disableRestoreFocus>
                                {activeLegend && activeLegend.bypassInfo && (
                                    <div style={{ padding: 8, maxWidth: 450, width: 450, position: 'relative' }}>
                                        <IconButton size="small" aria-label="close" onClick={() => { setActiveLegend(null); setLegendAnchorEl(null); }} style={{ position: 'absolute', top: 4, right: 4 }}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                        <CustomTooltip
                                            active={true}
                                            payload={[{ name: activeLegend.name, value: activeLegend.venueCount, payload: { fill: activeLegend.fill } }]} />
                                    </div>
                                )}
                            </Popover>
                        )}
                    </Paper>
                </Grid>;
            })}
        </>
    );
};

RCPieChart.defaultProps = {
    seriesList: [],
    data: [],
    width: 450,
    height: 450,
    legend: { layout: 'vertical', align: 'right', verticalAlign: 'middle', iconType: 'square' },
    elevation: 0,
    chartSize: 80,
    showLegendCounts: false,
    enableLegendClickTooltip: false
};
RCPieChart.propTypes = {
    /**Array contains definition of cell will be render in chart
     * Ex: seriesList={[
                      { dataKey: 'processingFailed', name: 'Processing Failed', fill: '#f44336'},
                      { dataKey: 'processed', name: 'Processed', fill: '#4caf50' }]}
    **dataKey: match for value of each cell in circle
    **fill: cell color
    **name: cell name which is displayed at Legend
    */
    seriesList: PropTypes.array,
    /**Array of data will be used for render */
    data: PropTypes.array,
    /**key for getting name of pie chart */
    dataKey: PropTypes.string,
    /**container width */
    width: PropTypes.number,
    /**container height */
    height: PropTypes.number,
    legend: PropTypes.object,
    /**background color weight: read [Paper] of MUI */
    elevation: PropTypes.number,
    /**Size of chart, default value is 80 */
    chartSize: PropTypes.number,
    /**Custom function to convert data for each pie chart item */
    convertData: PropTypes.func,
    /**Custom tooltip component for hover tooltip on pie segments */
    customTooltip: PropTypes.func,
    /**Show counts next to each legend item when seriesList item has venueCount*/
    showLegendCounts: PropTypes.bool,
    /**Enable clicking legend item to display the custom tooltip below chart*/
    enableLegendClickTooltip: PropTypes.bool
};
export default RCPieChart;